import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import AppEmpty from '@nxsys/components/AppEmpty';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { defaultAttachmentOderDesOptions } from '@nxsys/constants/AppConstants';
import { LoanTypesEnum } from '@nxsys/constants/AppEnums';
import { useGetPayrollSettingByEmployeeId } from '@nxsys/hooks/api/employee/useEmployeeApi';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatDateDisplay } from '@nxsys/utils/dateUtils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { find } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

enum OtherItemTabEnum {
  DEDUCT_STUDENT_LOAN = 'DEDUCT_STUDENT_LOAN',
  ATTACHMENT_ORDER = 'ATTACHMENT_ORDER',
}

const EmployeeOtherItem = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { attachmentOrderList } = useSelector(
    (state: AppState) => state.employeeStore,
  );
  const { loading, list } = attachmentOrderList;

  const employeeId = (user?.subcontractorId as any)?.id;

  const {
    run: getPayrollSettingByEmployeeId,
    value: payrollSettingValue,
    loading: payrollSettingLoading,
  } = useGetPayrollSettingByEmployeeId();

  useEffect(() => {
    if (employeeId) {
      dispatch(
        employeeActions.getAttachmentOrderEmployeeList({
          page: 0,
          size: 20,
          subcontractorId: employeeId,
        }),
      );
      getPayrollSettingByEmployeeId(employeeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, dispatch]);

  const payrollSetting = payrollSettingValue?.data;
  const haveStudentLoan = payrollSetting?.haveStudentLoan;

  const tabs = useMemo(() => {
    const tabList = [];
    if (haveStudentLoan) {
      tabList.push({
        tab: 'Deduct Student Loan',
        key: OtherItemTabEnum.DEDUCT_STUDENT_LOAN,
      });
    }
    tabList.push({
      tab: 'Attachment Order',
      key: OtherItemTabEnum.ATTACHMENT_ORDER,
    });
    return tabList;
  }, [haveStudentLoan]);

  const [activeTab, setActiveTab] = useState<OtherItemTabEnum>(
    haveStudentLoan
      ? OtherItemTabEnum.DEDUCT_STUDENT_LOAN
      : OtherItemTabEnum.ATTACHMENT_ORDER,
  );

  useEffect(() => {
    if (
      !haveStudentLoan &&
      activeTab === OtherItemTabEnum.DEDUCT_STUDENT_LOAN
    ) {
      setActiveTab(OtherItemTabEnum.ATTACHMENT_ORDER);
    }
  }, [haveStudentLoan, activeTab]);

  const handleTabChange = (key: OtherItemTabEnum) => {
    setActiveTab(key);
  };

  const deductStudentLoanData = useMemo(() => {
    if (!payrollSetting || !payrollSetting.checkedStudentPlans) return [];

    const plans = [];

    if (payrollSetting.checkedStudentPlans.includes(LoanTypesEnum.PLAN_1)) {
      plans.push({
        label: 'Plan 1',
        startDate: formatDateDisplay(payrollSetting.studentLoanPlan1StartDate),
        endDate: formatDateDisplay(payrollSetting.studentLoanPlan1EndDate),
      });
    }

    if (payrollSetting.checkedStudentPlans.includes(LoanTypesEnum.PLAN_2)) {
      plans.push({
        label: 'Plan 2',
        startDate: formatDateDisplay(payrollSetting.studentLoanPlan2StartDate),
        endDate: formatDateDisplay(payrollSetting.studentLoanPlan2EndDate),
      });
    }

    if (payrollSetting.checkedStudentPlans.includes(LoanTypesEnum.PLAN_4)) {
      plans.push({
        label: 'Plan 4',
        startDate: formatDateDisplay(payrollSetting.studentLoanPlan4StartDate),
        endDate: formatDateDisplay(payrollSetting.studentLoanPlan4EndDate),
      });
    }

    if (payrollSetting.checkedStudentPlans.includes(LoanTypesEnum.PGL)) {
      plans.push({
        label: 'Postgraduate Loan',
        startDate: formatDateDisplay(
          payrollSetting.studentLoanPostGraStartDate,
        ),
        endDate: formatDateDisplay(payrollSetting.studentLoanPostGraEndDate),
      });
    }

    return plans;
  }, [payrollSetting]);

  const attachmentOrderColumns: ColumnsType<IAttachmentOrder> = [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'deductionDesc',
      key: 'deductionDesc',
      render: (value: string) =>
        find(defaultAttachmentOderDesOptions, (item) => item.value === value)
          ?.label || '-',
    },
    {
      title: 'Amount',
      dataIndex: 'totalAttachment',
      key: 'totalAttachment',
      align: 'right',
      // NX-5642: empty/0 total means the order has no cap, so show '_'.
      render: (value: number) =>
        value > 0
          ? formatMoney(value, showCurrencyByCountry(payrollCountry))
          : '_',
    },
    {
      title: 'Deduction',
      dataIndex: 'totalDeduction',
      key: 'totalDeduction',
      align: 'right',
      render: (value: number) =>
        formatMoney(value || 0, showCurrencyByCountry(payrollCountry)),
    },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case OtherItemTabEnum.DEDUCT_STUDENT_LOAN:
        if (payrollSettingLoading) {
          return (
            <div className="px-[20px] mt-[12px]">
              <Skeleton active />
            </div>
          );
        }
        return (
          <div className="px-[20px] mt-[12px] space-y-[16px]">
            {deductStudentLoanData.length > 0 ? (
              deductStudentLoanData.map((item) => (
                <div
                  key={item.label}
                  className="border border-neutral-100 p-[12px] bg-violet-50 rounded-[8px] flex justify-between"
                >
                  <div className="text-[14px] font-[500] text-neutral-700 flex-1">
                    {item.label}
                  </div>
                  <div className="flex-1 space-y-[14px]">
                    <div className="flex items-center justify-between">
                      <div className="text-[14px] font-[400] text-neutral-400">
                        Start Date
                      </div>
                      <div className="text-[14px] font-[500] text-neutral-700">
                        {item.startDate}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-[14px] font-[400] text-neutral-400">
                        End Date
                      </div>
                      <div className="text-[14px] font-[500] text-neutral-700">
                        {item.endDate}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <AppEmpty />
            )}
          </div>
        );
      case OtherItemTabEnum.ATTACHMENT_ORDER:
        return (
          <div className="mt-[12px]">
            <AppTableContainer
              rowKey="id"
              data={list ?? []}
              loading={loading}
              page={1}
              total={list?.length ?? 0}
              pageSize={10}
              pagination={false}
              scroll={{ x: 300, y: 400 }}
              columns={attachmentOrderColumns}
            />
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <AppCard className="h-full">
      <div className="px-[20px] pt-[20px]">
        <div className="text-[16px] font-[500]">Other Item</div>
      </div>
      <AppCardTabListContainer
        tabList={tabs}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        className="!border-none"
      >
        {renderTabContent()}
      </AppCardTabListContainer>
    </AppCard>
  );
};

export default EmployeeOtherItem;
